import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import {
  confirmReferralSuccess,
  referralGetDetails,
  referralsGetPreviouslyRedeemed,
  referralRedeem,
} from '../redux/modules/friends'
import { Article } from '../components/common/Layout'
import { CopyReferral } from '../components/common/FriendRequest'
import { loadOrderHistory } from '../api/api'
import { Action, Button, Input, InputError } from '@foodsby/nutrient'
import { Card } from '@foodsby/nutrient-v3'
import { homeRoute } from '../routes/routes'
import { Link, Redirect } from 'react-router-dom'
import SimpleSpinner from '../components/common/SimpleSpinner'
import CollectEmailPanel from '../components/home/CollectEmailPanel'
import { pushAnalyticsEvent } from '../util/gtmUtils'
import { SuccessModal } from '../components/success-modal/SuccessModal'

const ReferralError = ({ title, message, isAuthenticated, locationId }) => {
  const [hideNotification, setHideNotification] = useState(false)

  useEffect(() => {
    pushAnalyticsEvent('Referral', 'Referral Error')
  }, [])

  return (
    <>
      {!hideNotification && (
        <SuccessModal
          isFailure
          onClose={() => setHideNotification(true)}
          title={title}
        />
      )}
      <Card className="referral-card">
        <p>{message}</p>
        {isAuthenticated && (
          <div className="row center-xs referral-copy">
            <div className="col-xs-12">
              <CopyReferral />
            </div>
          </div>
        )}
        {locationId && (
          <div className="row center-xs">
            <div className="col-xs-12">
              <Link to={homeRoute.path}>
                <Action>View today's deliveries</Action>
              </Link>
            </div>
          </div>
        )}
      </Card>
    </>
  )
}

const GetOrEnterReferralCode = ({ setReferral, search }) => {
  const [inputValue, setInputValue] = useState()
  const [inputError, setInputError] = useState()
  const [loading, setLoading] = useState(true)

  const getReferralDetails = async queryParamCode => {
    setLoading(true)
    const details = await referralGetDetails(inputValue || queryParamCode)
    setLoading(false)
    if (details) {
      setReferral(details)
    } else {
      setInputError(
        '🤔 This code doesn’t seem to be working. Please try entering a valid code.',
      )
    }
  }

  const isValidReferralCode = code => {
    const format = /[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/ //eslint-disable-line
    if (code?.length < 7 || code?.length > 7 || format.test(code)) {
      return false
    } else {
      return true
    }
  }

  useEffect(() => {
    setLoading(true)
    const params = new URLSearchParams(search)
    const referralCode = params.get('token')
    if (referralCode && isValidReferralCode(referralCode)) {
      getReferralDetails(referralCode)
      pushAnalyticsEvent('Referral', 'Followed Link')
    } else {
      setLoading(false)
      pushAnalyticsEvent('Referral', 'Followed Manual Entry')
    }
  }, []) //eslint-disable-line

  return (
    <Card className="referral-card">
      {loading ? (
        <SimpleSpinner />
      ) : (
        <div className="enter-referral">
          <h4>Enter Your Referral Code</h4>
          <div className="row middle-xs referral-input">
            <div className="col-xs-12 start-xs">
              <label className="referral-code-label" htmlFor="referral">
                Referral Code *
              </label>
            </div>
            <div className="col-xs-12 col-sm-10">
              <Input
                id="referral"
                onBlur={evt => {
                  setInputValue(evt.target.value)
                }}
                onChange={evt => {
                  setInputValue(evt.target.value)
                }}
                onKeyDown={e => {
                  if (e.keyCode === 13) {
                    e.preventDefault()
                    if (!isValidReferralCode(inputValue)) {
                      setInputError('Referral code must be 7 digits.')
                    } else {
                      getReferralDetails()
                    }
                  }
                }}
                type="text"
              />
            </div>
            <div className="col-xs-12 col-sm-2 center-xs end-sm">
              <Button
                className="secondary"
                onClick={event => {
                  event.preventDefault()
                  if (!isValidReferralCode(inputValue)) {
                    setInputError('Referral code must be 7 digits.')
                  } else {
                    getReferralDetails()
                  }
                }}
              >
                Redeem
              </Button>
            </div>
            <div className="col-xs-12 center-xs start-sm">
              {inputError && <InputError>{inputError}</InputError>}
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}

const RegisterReferral = ({ locationId }) => {
  useEffect(() => {
    pushAnalyticsEvent('Referral', 'Registration Form Shown')
  }, [])

  return (
    <Card className="referral-card">
      <div className="row register-referral">
        <div className="col-xs-12">
          <h4>Way to go!</h4>
          <p>
            That referral checks out. Now create an account and get a credit to
            use on your first order.
          </p>
        </div>
        <div className="col-xs-12 collect-email">
          <CollectEmailPanel entryPoint="REFERRAL" locationId={locationId} />
        </div>
      </div>
    </Card>
  )
}

const RedeemReferral = ({
  confirmReferralSuccess,
  referralCode,
  setRedemptionError,
  redeemerUserId,
  referrerUserId,
}) => {
  const [redeemed, setRedeemed] = useState()
  const [loading, setLoading] = useState(true)
  const [acknowledged, setAcknowledged] = useState()
  const [firedEvents, setFiredEvents] = useState()

  const redeemReferral = async () => {
    try {
      await referralRedeem(referralCode)
      setRedeemed(true)
    } catch (e) {
      setRedeemed(false)
      setRedemptionError('We had trouble processing that referral.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (referralCode) {
      redeemReferral()
      confirmReferralSuccess()
      setAcknowledged(true)
    }
  }, [referralCode]) //eslint-disable-line

  useEffect(() => {
    if (!firedEvents && redeemed && acknowledged) {
      pushAnalyticsEvent('Referral', 'Redeemed')
      if (window.appboy) {
        referrerUserId &&
          window.appboy.logCustomEvent('referral-user-referred', {
            userId: referrerUserId,
          })
        redeemerUserId &&
          window.appboy.logCustomEvent('referral-user-redeemed', {
            userId: redeemerUserId,
          })
      }
      setFiredEvents(true)
    }
  }, [acknowledged, firedEvents, redeemed, redeemerUserId, referrerUserId])

  return (
    <Card className="referral-card">
      {loading ? (
        <SimpleSpinner />
      ) : (
        redeemed && acknowledged && <Redirect to={homeRoute.path} />
      )}
    </Card>
  )
}

export const PageReferral = ({
  isAuthenticated,
  isCurrentUserLoading,
  confirmReferralSuccess,
  currentUser,
  location: { search },
}) => {
  const [referral, setReferral] = useState() // not set: undefined. No return: null
  const [userQualifies, setUserQualifies] = useState()
  const [userQualifiesError, setUserQualifiesError] = useState()
  const [redemptionError, setRedemptionError] = useState()

  const getCanUserRedeem = async (currentUser, referral) => {
    try {
      let failedCheck = false
      // is own referral code?
      if (currentUser && referral && currentUser.userId === referral.userId) {
        failedCheck = true
        setUserQualifies(false)
        setUserQualifiesError(`Sorry, you can't redeem from your code`)
      }

      // has previous redemption?
      if (!failedCheck) {
        const prevRedeemed = await referralsGetPreviouslyRedeemed()
        if (prevRedeemed) {
          failedCheck = true
          setUserQualifies(false)
          setUserQualifiesError(`Sorry, you can't redeem more than one`)
        }
      }

      // has previous orders?
      if (!failedCheck) {
        const prevOrders = await loadOrderHistory()
        if (prevOrders && prevOrders.orders && prevOrders.orders.length > 0) {
          failedCheck = true
          setUserQualifies(false)
          setUserQualifiesError(
            'Sorry, but the referral incentive is only for new users.',
          )
        }
      }

      if (failedCheck === false) {
        setUserQualifies(true)
        pushAnalyticsEvent('Referral', 'Qualifies True')
      } else {
        pushAnalyticsEvent('Referral', 'Qualifies False')
      }
    } catch (e) {
      setUserQualifies(false)
      setUserQualifiesError(
        'An internal server error occurred processing the referral.',
      )
      pushAnalyticsEvent('Referral', 'Qualifies False')
    }
  }

  useEffect(() => {
    if (
      referral &&
      isAuthenticated &&
      currentUser &&
      userQualifies === undefined
    ) {
      getCanUserRedeem(currentUser, referral)
    }
  }, [currentUser, isAuthenticated, referral, userQualifies]) //eslint-disable-line

  return (
    <Article classnames="WebConV3">
      {/* Step 1 - Get referral details (manual or via query param) */}
      {referral === undefined && (
        <GetOrEnterReferralCode search={search} setReferral={setReferral} />
      )}

      {/* Step 2 - create an account (skips if logged in) */}
      {referral && !isAuthenticated && (
        <RegisterReferral locationId={referral?.locationId} />
      )}

      {/* Step 3 - check if user qualifies to redeem a referral (no prev redemption, no prev orders, not own referral)  */}
      {referral && isAuthenticated && userQualifies === undefined ? (
        <SimpleSpinner />
      ) : (
        userQualifies === false &&
        userQualifiesError && (
          <ReferralError
            isAuthenticated={isAuthenticated}
            locationId={currentUser?.deliveryLocationId || referral?.locationId}
            message="Share your own referral link and get credits for each new user when they place their first order and it gets delivered."
            title={userQualifiesError}
          />
        )
      )}

      {/* Step 4 - Redeem referral and redirect to home */}
      {userQualifies === true && !redemptionError ? (
        <RedeemReferral
          confirmReferralSuccess={confirmReferralSuccess}
          redeemerUserId={currentUser?.userId}
          referralCode={referral?.referralCode}
          referrerUserId={referral?.userId}
          setRedemptionError={setRedemptionError}
        />
      ) : (
        redemptionError && (
          <ReferralError
            isAuthenticated={isAuthenticated}
            locationId={currentUser?.deliveryLocationId || referral?.locationId}
            message="Try following the referral link again. If this message persists, please contact support."
            title={userQualifiesError}
          />
        )
      )}
    </Article>
  )
}

const mapStateToProps = state => {
  const { isAuthenticated, isCurrentUserLoading, currentUser } = state.user
  return {
    isAuthenticated,
    isCurrentUserLoading,
    currentUser,
  }
}

const mapDispatchToProps = { confirmReferralSuccess }

export default connect(mapStateToProps, mapDispatchToProps)(PageReferral)
